import { useAction, useAtom } from '@reatom/npm-react'
import { memo } from 'react'
import styled from 'styled-components'

import { Numeric, Skeleton } from '@blade/ui-kit'
import { TierIcon } from '@root/entities/premium/ui'
import { openSplashPointsInfoFlowEvent } from '@root/entities/splash-point'
import { PREMIUM_ENABLED } from '@root/shared'

import { PairBoosters } from './pair-boosters'

import type { CurrencyModel } from '@root/entities/currency'
import type { PremiumMetaModel } from '@root/entities/premium/atoms'

type Mode = 'trade' | 'explore'

export type PairBoostersBadgeProps = {
  currency: CurrencyModel
  premiumInfo: PremiumMetaModel
  /**
   * Allow open boost dialog from the boosters
   */
  withBoost?: boolean
  mode?: Mode
}

export const PairBoostersBadge = memo(
  ({
    currency,
    premiumInfo,
    mode = 'trade',
    withBoost = false,
  }: PairBoostersBadgeProps) => {
    const [splashPoints] = useAtom(premiumInfo.splashPoints)
    const [tier] = useAtom(premiumInfo.tier)
    const [boosters] = useAtom(premiumInfo.boosters)

    const openSplashPointsInfo = useAction(openSplashPointsInfoFlowEvent)

    const handleOpenSplashPointsInfo = (
      e: React.MouseEvent<HTMLDivElement>,
    ) => {
      e.stopPropagation()
      e.preventDefault()
      openSplashPointsInfo(currency)
    }

    // Boosters and splash points are Premium, which is hidden until it is
    // sold — and a "Boost" button that opens a purchase nobody can complete
    // is worse than no button.
    if (!PREMIUM_ENABLED || (!splashPoints && !boosters && !withBoost)) {
      return undefined
    }

    const withSplashPoints = splashPoints > 0

    return (
      <Root $mode={mode}>
        <PairBoosters
          currency={currency}
          premiumInfo={premiumInfo}
          mode={mode}
          withBoost={withBoost}
        />
        {withSplashPoints ? (
          <Points onClick={handleOpenSplashPointsInfo}>
            <TierIcon tier={tier} size={10} />
            <Numeric value={splashPoints} preset="splash-points" />
          </Points>
        ) : undefined}
      </Root>
    )
  },
)

const Root = styled.div<{ $mode: Mode }>`
  background: var(--boosters-badge-bg, var(--backgroundA-3));

  align-items: center;
  display: grid;
  border-radius: 10px;
  justify-content: start;
  width: min-content;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  height: 17px;
`

const Points = styled.div`
  color: var(--text-12);
  pointer-events: auto;
  cursor: pointer;
  text-align: right;
  display: flex;
  align-items: center;

  font-size: 10px;
  font-weight: 800;
  padding-inline: 4px 6px;
  gap: 2px;
`

const Skeleton_ = styled(Skeleton)`
  border-radius: 10px;
`

export const PairBoostersBadgeSkeleton = () => (
  <Skeleton_ width={104} height={20} />
)
